"use client";

import { useCallback } from "react";

import { addToListsButtonState } from "@/components/lists/add-to-lists-button-state";
import { AddToListsButtonView } from "@/components/lists/add-to-lists-button-view";
import { useLists } from "@/contexts/lists-context";
import { useHydrated } from "@/hooks/use-hydrated";
import { useTrackingScope } from "@/hooks/use-tracking-scope";
import { itemQueryForAddToListsTarget, type AddToListsTarget } from "@/lib/add-to-lists-target";

interface AddToListsButtonProps {
  target: AddToListsTarget;
  variant?: "icon" | "button" | "full";
  className?: string;
}

export function AddToListsButton({ target, variant = "button", className }: AddToListsButtonProps) {
  const { isInCollection, isInWishlist, openAddToLists } = useLists();
  const hydrated = useHydrated();
  const trackingScope = useTrackingScope();

  const item = itemQueryForAddToListsTarget(target);
  const inCollection = hydrated && isInCollection(item);
  const inWishlist = hydrated && isInWishlist(item);

  const handleClick = useCallback(
    (e?: React.MouseEvent) => {
      e?.preventDefault();
      e?.stopPropagation();
      openAddToLists({ target, trackingScope });
    },
    [openAddToLists, target, trackingScope],
  );

  return (
    <AddToListsButtonView
      state={addToListsButtonState(inCollection, inWishlist)}
      variant={variant}
      className={className}
      onClick={handleClick}
    />
  );
}
